"use client";

import { LogOut, User } from "lucide-react";
import { useAuthSession } from "@/lib/auth/auth-provider";
import { clearAuthState } from "@/lib/auth-store";
import { clearAuthRedirectLock, markLogoutIntent } from "@/lib/auth-client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export function NavUser() {
  const { session, status, logout } = useAuthSession();
  const { isMobile } = useSidebar();

  const userName = session?.user?.name;
  const userEmail = session?.user?.email;

  const nameParts = userName ? userName.split(" ") : [];
  const userProfile: UserProfile | null = status === "authenticated" && userName
      ? {
        id: userEmail || userName || "unknown",
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: userEmail || "",
      }
      : null;

  const handleLogout = async () => {
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const callbackUrl =
      currentPath.startsWith("/") && currentPath !== "/"
        ? currentPath
        : "/task-list";
    clearAuthState();
    clearAuthRedirectLock();
    markLogoutIntent();
    await logout(callbackUrl);
  };

  if (status === "loading") {
    return (
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton disabled size="lg">
              <div className="size-8 shrink-0 rounded-lg bg-gray-200 animate-pulse" />
              <div className="grid min-w-0 flex-1 gap-1 group-data-[collapsible=icon]:hidden">
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
    );
  }

  if (!userProfile) return null;

  const displayName = userProfile.firstName && userProfile.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`
      : userProfile.firstName || userProfile.id;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
                size="lg"
                aria-label={`Menú de usuario: ${displayName}`}
                className="group-data-[collapsible=icon]:justify-center data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted group-data-[collapsible=icon]:bg-transparent">
                <User className="h-4 w-4" />
              </div>
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{displayName}</span>
                {userProfile.email && (
                    <span className="truncate text-xs">{userProfile.email}</span>
                )}
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <User className="h-4 w-4" />
                </div>
                <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{displayName}</span>
                  {userProfile.email && (
                      <span className="truncate text-xs">{userProfile.email}</span>
                  )}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
